/**
 * DEDICATED Video Meeting Monitor Service
 * Calls the dedicated Cloud Function every 2 minutes for real-time meeting end detection
 * Completely isolated from other systems for zero impact
 */

class VideoMeetingMonitorService {
  constructor() {
    this.monitorInterval = null;
    this.isRunning = false;
    this.monitoringUrl = null;
    this.lastCheck = null;
    this.consecutiveErrors = 0;
    this.maxConsecutiveErrors = 3;
  }

  /**
   * Initialize the service with the appropriate Cloud Function URL
   */
  initialize() {
    // Use the correct Firebase Functions v2 URL
    this.monitoringUrl = 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/videoMeetingMonitor';
    console.log('🎥 Video Meeting Monitor Service initialized with URL:', this.monitoringUrl);
  }

  /**
   * Start monitoring video meetings
   * Calls the dedicated Cloud Function every 2 minutes
   */
  start() {
    if (this.isRunning) {
      console.log('🎥 Video meeting monitoring already running');
      return;
    }

    if (!this.monitoringUrl) {
      this.initialize();
    }

    console.log('🎥 Starting ACCELERATED video meeting monitoring (every 30 seconds)');
    this.isRunning = true;
    this.consecutiveErrors = 0;

    // Initial check
    this.performMonitoringCheck();

    // Set up periodic monitoring every 30 seconds for IMMEDIATE detection
    this.monitorInterval = setInterval(() => {
      this.performMonitoringCheck();
    }, 30 * 1000); // 30 seconds - ACCELERATED

    return this;
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
    
    this.isRunning = false;
    console.log('🎥 Video meeting monitoring stopped');
  }

  /**
   * Perform a single monitoring check
   */
  async performMonitoringCheck() {
    try {
      // Only log every 5th check to reduce console spam (once per minute in aggressive mode)
      this.checkCount = (this.checkCount || 0) + 1;
      const shouldLog = this.checkCount % 5 === 0;
      
      if (shouldLog) {
        console.log('🎥 Performing video meeting monitor check... (showing every 5th check)');
      }
      this.lastCheck = new Date();

      // Add timestamp to force cache busting
      const cacheBustUrl = `${this.monitoringUrl}?t=${Date.now()}`;
      
      const response = await fetch(cacheBustUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({}),
        timeout: 25000,
        cache: 'no-cache'
      });
      
      // Only log response details if there's an error or events detected
      const isError = !response.ok;
      if (isError || shouldLog) {
        console.log('📡 Response status:', response.status);
      }

      if (response.ok) {
        const responseText = await response.text();
        
        try {
          const result = JSON.parse(responseText);
          // Only log if events detected, errors, or every 5th check
          const hasEvents = result.endEventsDetected > 0;
          if (hasEvents || shouldLog) {
            console.log(`✅ Video monitoring check completed - ${result.endEventsDetected} end events detected`);
          }
          if (hasEvents) {
            console.log('📄 Raw response with events:', responseText);
          }
          
          // Reset error counter on success
          this.consecutiveErrors = 0;
        } catch (parseError) {
          console.error('❌ JSON parse error. Raw response:', responseText);
          this.handleError(parseError);
        }
      } else {
        const errorText = await response.text();
        console.error(`⚠️ HTTP ${response.status} response:`, errorText);
        this.handleError(new Error(`HTTP ${response.status}: ${errorText.substring(0, 200)}`));
      }

    } catch (error) {
      console.error('❌ Video monitoring check error:', error);
      this.handleError(error);
    }
  }

  /**
   * Handle monitoring errors with backoff
   */
  handleError(error) {
    this.consecutiveErrors++;
    
    if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
      console.error(`🚨 Video monitoring: ${this.consecutiveErrors} consecutive errors - stopping monitoring`);
      this.stop();
      
      // Attempt to restart after 5 minutes
      setTimeout(() => {
        console.log('🔄 Attempting to restart video monitoring after error cooldown');
        this.start();
      }, 5 * 60 * 1000);
    } else {
      console.warn(`⚠️ Video monitoring error (${this.consecutiveErrors}/${this.maxConsecutiveErrors}):`, error.message);
    }
  }

  /**
   * Get monitoring status
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      lastCheck: this.lastCheck,
      consecutiveErrors: this.consecutiveErrors,
      monitoringUrl: this.monitoringUrl
    };
  }

  /**
   * Manual trigger for testing
   */
  async triggerCheck() {
    console.log('🧪 Manual video monitoring check triggered');
    await this.performMonitoringCheck();
  }

  /**
   * AGGRESSIVE monitoring - check every 10 seconds for immediate results
   */
  startAggressiveMode() {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    
    console.log('🚨 AGGRESSIVE MODE: Video monitoring every 10 seconds');
    this.isRunning = true;
    
    // Initial check
    this.performMonitoringCheck();
    
    // Super aggressive monitoring every 10 seconds
    this.monitorInterval = setInterval(() => {
      this.performMonitoringCheck();
    }, 10 * 1000); // 10 seconds
  }
}

// Create singleton instance
const videoMeetingMonitorService = new VideoMeetingMonitorService();

export default videoMeetingMonitorService; 